import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from './utils';
import './OpportunitySheetModal.css';
import { CheckIcon, XMarkIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";


const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];
const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Completed'];

interface Opportunity {
  project_name: string;
  status: string;
  start_date: string | null;
  due_date: string | null;
  priority: string;
  created_by: string;
  created_date: string;
  opportunity_id: string;
  region_code: string;
  progress_status: string;
}

interface Label {
  label_id: string;
  label_name: string;
  opportunity_label_id?: string;
}

interface OpportunitySheetModalProps {
  opportunity: Opportunity;
  onClose: () => void;
  onSaveSuccess?: () => void;
}
interface OpportunityItem {
  item_id: string;
  item_description: string;
  opportunity_id: string;
  item_rank?: number;
}

interface Owner {
  opportunity_manning_id: number;
  opportunity_id: number;
  manning_id: number;
  manning_name: string;
  manning_type: string;
}


interface Manning {
  manning_info_id: number;
  name: string;
}


const OpportunitySheetModal: React.FC<OpportunitySheetModalProps> = ({
  opportunity,
  onClose,
  onSaveSuccess,
}) => {
  const [editedPriority, setEditedPriority] = useState('');
  const [editedStartDate, setEditedStartDate] = useState('');
  const [editedDueDate, setEditedDueDate] = useState('');
  const [note, setNote] = useState('');
  const [loadingNote, setLoadingNote] = useState(false);
  const [currentNoteId, setCurrentNoteId] = useState<string | null>(null);
  const [editedStatus, setEditedStatus] = useState(opportunity.progress_status || 'Not Started');
  const [allLabels, setAllLabels] = useState<Label[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [itemInput, setItemInput] = useState('');
  const [items, setItems] = useState<OpportunityItem[]>([]);
  const [addingItem, setAddingItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [owners, setOwners] = useState<Owner[]>([]);
  const [ownerInput, setOwnerInput] = useState('');
  const [manningList, setManningList] = useState<Manning[]>([]);
const [selectedManningId, setSelectedManningId] = useState<number | ''>('');


  useEffect(() => {
    setEditedPriority(opportunity.priority || '');
    setEditedStartDate(opportunity.start_date || '');
    setEditedDueDate(opportunity.due_date || '');
    setEditedStatus(opportunity.progress_status || 'Not Started');
    fetchNotes();
    fetchAllLabels();
    fetchLabels();
    fetchItems();
    fetchOwners();
    // eslint-disable-next-line
  }, [opportunity.opportunity_id]);


const fetchItems = async () => {
  try {
    const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_opportunity_items&opportunity_id=${encodeURIComponent(opportunity.opportunity_id)}`;
    const response = await fetchWithAuth(url); // default is GET
    setItems(response || []);
  } catch (e) {
    setItems([]);
  }
};

const fetchOwners = async () => {
  try {
    const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_opportunity_mannings_by_type&opportunity_id=${opportunity.opportunity_id}&manning_type=OWNER`;
    const response = await fetchWithAuth(url);
    setOwners(response || []);
  } catch (e) {
    console.error('Failed to fetch owners', e);
    setOwners([]);
  }
};


  // Fetch all possible label options
  const fetchAllLabels = async () => {
    const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_labels';
    const data = await fetchWithAuth(url);
    setAllLabels(data);
  };

  // Fetch labels assigned to this opportunity
  const fetchLabels = async () => {
    const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_opportunity_labels&opportunity_id=${opportunity.opportunity_id}`;
    const data = await fetchWithAuth(url);
    setLabels(data);
  };

const handleAddOwner = async (manningId: number) => {
  if (!manningId) return;

  // Prevent duplicates
  if (owners.some(o => o.manning_id === manningId)) return;

  const selected = manningList.find(m => m.manning_info_id === manningId);
  if (!selected) return;

  try {
    const baseUrl =
      'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==';

    /* --------------------------------------------------
       1) ADD OPPORTUNITY MANNING (BLOCKING)
    -------------------------------------------------- */
    const addResponse = await fetchWithAuth(
      `${baseUrl}&action=add_opportunity_manning`,
      {
        method: 'POST',
        body: JSON.stringify({
          opportunity_id: opportunity.opportunity_id,
          manning_id: selected.manning_info_id,
          manning_name: selected.name,
          manning_type: 'OWNER',
        }),
      }
    );

    if (!addResponse || addResponse.status === 'failure') {
      throw new Error('add_opportunity_manning failed');
    }

    // Update UI immediately
    setSelectedManningId('');
    fetchOwners();

    /* --------------------------------------------------
       2) PROCESS OWNER ASSIGNMENT (NON-BLOCKING)
    -------------------------------------------------- */
    fetchWithAuth(
      `${baseUrl}&action=process_opportunity_owner_assignment`,
      {
        method: 'POST',
        body: JSON.stringify({
          opportunity_id: opportunity.opportunity_id,
          manning_id: selected.manning_info_id,
        }),
      }
    ).catch(err => {
      // Log only — do NOT impact UI
      console.error(
        'Background owner assignment processing failed:',
        err
      );
    });

  } catch (e) {
    console.error(e);
    alert('Failed to add owner');
  }
};



  // Add label to opportunity
  const handleAddLabel = async (labelName: string) => {
    if (!opportunity) return;
    try {
      const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=add_opportunity_label';
      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify({
          opportunity_id: opportunity.opportunity_id,
          label_name: labelName,
        }),
      });
      if (response) {
        fetchLabels();
      } else {
        console.error('Failed to add label');
      }
    } catch (error) {
      console.error('Error adding label:', error);
    }
  };

  // Remove label from opportunity
  const handleRemoveLabel = async (opportunityLabelId: string) => {
    try {
      const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=remove_opportunity_label';
      await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify({
          opportunity_label_id: opportunityLabelId,
        }),
      });
      fetchLabels();
    } catch (error) {
      console.error('Error removing label:', error);
    }
  };

  // Get color for label (your palette or pastel)
  const getColorForLabel = (label: Label | string) => {
    const labelText = typeof label === 'string' ? label : label.label_name;
    const labelColors: { [key: string]: string } = {
      'PO in Hand': '#0078D7',  // Blue
      'In Progress': '#FF5733', // Orange
      'Completed': '#28A745',   // Green
      'Pending': '#FFC107',     // Yellow
      'On Hold': '#8E44AD',     // Purple
      'Cancelled': '#E74C3C',   // Red
      'Reviewed': '#17A2B8',    // Light Blue
      'Approved': '#2ECC71',    // Light Green
      // Add more as needed
    };
    return labelColors[labelText] || generateRandomDarkColor(labelText);
  };

  // Generate a consistent pastel/dark color for unknown labels
  function generateRandomDarkColor(labelText: string) {
    // Create hash from labelText for consistent color
    let hash = 0;
    for (let i = 0; i < labelText.length; i++) {
      hash = labelText.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Use HSL: darker and more pastel
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 55%, 55%)`;
  }

  // Notes fetch/save logic
  const fetchNotes = async () => {
    setLoadingNote(true);
    try {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_opportunity_notes&opportunity_id=${opportunity.opportunity_id}`;
      const data = await fetchWithAuth(url);
      if (Array.isArray(data) && data.length > 0) {
        const combinedNotes = data.map((item) => item.note).join('\n\n');
        setNote(combinedNotes);
        setCurrentNoteId(data[0]?.opportunity_note_id || null);
      } else {
        setNote('');
        setCurrentNoteId(null);
      }
    } catch (e) {
      setNote('');
      setCurrentNoteId(null);
    } finally {
      setLoadingNote(false);
    }
  };

  const handleSaveNote = async () => {
    if (!opportunity) return;
    try {
      if (!currentNoteId) {
        const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=add_opportunity_note';
        await fetchWithAuth(url, {
          method: 'POST',
          body: JSON.stringify({
            opportunity_id: opportunity.opportunity_id,
            note,
            note_added_by: 'test user',
          }),
        });
      } else {
        const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_opportunity_note';
        await fetchWithAuth(url, {
          method: 'POST',
          body: JSON.stringify({
            opportunity_note_id: currentNoteId,
            note,
            note_updated_by: 'test user',
          }),
        });
      }
    } catch (error) {
      alert('Failed to save note' + error);
    } finally {
      fetchNotes();
    }
  };

const handleSave = async () => {
  setSaving(true);
  setSaveMessage('');
  const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_opportunity`;
  try {
    await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({
        opportunity_id: opportunity.opportunity_id,
        status: editedStatus,
        priority: editedPriority,
        start_date: editedStartDate || null,
        due_date: editedDueDate || null,
      }),
    });
    await handleSaveNote();
    // Remove this to prevent closing the modal:
    // if (onSaveSuccess) onSaveSuccess();
    setSaveMessage('Saved Successfully');
  } catch (e) {
    alert('Error saving opportunity.');
  } finally {
    setSaving(false);
    setTimeout(() => setSaveMessage(''), 2000);
  }
};

const handleSaveAndClose = async () => {
  setSaving(true);
  setSaveMessage('');
  const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_opportunity`;
  try {
    await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({
        opportunity_id: opportunity.opportunity_id,
        status: editedStatus,
        priority: editedPriority,
        start_date: editedStartDate || null,
        due_date: editedDueDate || null,
      }),
    });
    await handleSaveNote();
    // Remove this to prevent closing the modal:
    if (onSaveSuccess) onSaveSuccess();
    setSaveMessage('Saved Successfully');
  } catch (e) {
    alert('Error saving opportunity.');
  } finally {
    setSaving(false);
    setTimeout(() => setSaveMessage(''), 2000);
  }
};


  const handleAddItem = async () => {
  const trimmed = itemInput.trim();
  if (!trimmed) return;
  setAddingItem(true);
  try {
    const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=add_opportunity_item';
    await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({
        opportunity_id: opportunity.opportunity_id,
        item_description: trimmed,
      }),
    });
    setItemInput('');
    fetchItems();
  } catch (e) {
    alert('Failed to add item');
  } finally {
    setAddingItem(false);
  }
};

const handleEditItem = (item: OpportunityItem) => {
  setEditingItemId(item.item_id);
  setEditingText(item.item_description);
};

const handleSaveEditItem = async (itemId: string) => {
  if (!editingText.trim()) return;
  try {
    const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_opportunity_item';
    await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({
        item_id: itemId,
        item_description: editingText.trim(),
      }),
    });
    setEditingItemId(null);
    setEditingText('');
    fetchItems();
  } catch (e) {
    alert('Failed to update item');
  }
};

const handleCancelEdit = () => {
  setEditingItemId(null);
  setEditingText('');
};

const handleDeleteItem = async (itemId: string) => {
  setDeletingItemId(itemId);
  try {
    const url =
      'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=delete_opportunity_item';
    await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId }),
    });
    fetchItems();
  } catch (e) {
    alert('Failed to delete item');
  } finally {
    setDeletingItemId(null);
  }
};

// Inside your component

const handleRankToggle = async (item: OpportunityItem) => {
  const newRank = item.item_rank === 1 ? 0 : 1;
  try {
    const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_opportunity_item';
    await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({
        item_id: item.item_id,
        rank: newRank,
      }),
    });
    fetchItems();
  } catch (e) {
    alert('Failed to update rank');
  }
};

const handleRemoveOwnerDblClick = async (ownerId: number) => {
  const prev = owners;
  setOwners(prev.filter(o => o.opportunity_manning_id !== ownerId));

  try {
    const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=remove_opportunity_manning';
    await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({
        opportunity_manning_id: ownerId
      }),
    });
    // Optionally re-fetch
    // await fetchOwners();
  } catch (e) {
    alert('Failed to remove owner');
    setOwners(prev);
  }
};

useEffect(() => {
  const cachedManning = localStorage.getItem('all_manning');
  if (cachedManning) {
    setManningList(JSON.parse(cachedManning));
  } else {
    const fetchManning = async () => {
      try {
        const url =
          'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_all_manning_pros';
        const data = await fetchWithAuth(url);
        setManningList(data);
        localStorage.setItem('all_manning', JSON.stringify(data));
      } catch (error) {
        console.error('Error fetching manning:', error);
      }
    };
    fetchManning();
  }
}, []);


  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <section className="modal-header-row">
          <h2 className="modal-title">{opportunity.project_name}</h2>
          
<div className="owner-initials-container">
  {owners.map((owner) => {
    const parts = owner.manning_name.trim().split(' ');
    const initialsText = parts.length >= 2
      ? parts[0][0] + parts[1][0]
      : parts[0][0];
    const bgColor = generateRandomDarkColor(owner.manning_name);

    return (
      <div
        key={owner.opportunity_manning_id}
        className="owner-circle"
        style={{ backgroundColor: bgColor }}
        title={`${owner.manning_name} (double-click to remove)`}
        onDoubleClick={() => handleRemoveOwnerDblClick(owner.opportunity_manning_id)}
      >
        {initialsText.toUpperCase()}
      </div>
    );
  })}
</div>



          <button onClick={onClose} className="modal-close" aria-label="Close">
            ×
          </button>
        </section>
        <section style={{ marginTop: 8 }}>
          <select
            className="modal-select"
            value={selectedManningId}
            onChange={e => {
              const id = Number(e.target.value);
              setSelectedManningId(id);
              handleAddOwner(id);
            }}
          >
            <option value="">Add Project POC...</option>
            {manningList.map(m => (
              <option key={m.manning_info_id} value={m.manning_info_id}>
                {m.name}
              </option>
            ))}
          </select>

        </section>

        <section>
          <div className="row-fields-grid">
            <div>
              <label className="field-label" htmlFor="priority-select">Priority</label>
              <select
                id="priority-select"
                value={editedPriority}
                onChange={e => setEditedPriority(e.target.value)}
                className="modal-select"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="progress-select">Progress</label>
              <select
                id="progress-select"
                value={editedStatus}
                onChange={e => setEditedStatus(e.target.value)}
                className="modal-select"
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="label-select">Add Label</label>
              <select
                id="label-select"
                className="modal-select"
                value={selectedLabel}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  if (selectedValue) {
                    handleAddLabel(selectedValue);
                    setSelectedLabel('');
                  } else {
                    setSelectedLabel(selectedValue);
                  }
                }}
              >
                <option value="">Select</option>
                {allLabels.map((label) => (
                  <option key={label.label_id} value={label.label_name}>
                    {label.label_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Label pill display */}
          <ul className="label-list">
            {labels.map((label, index) => (
              <li
                key={index}
                className="label-box"
                style={{ backgroundColor: getColorForLabel(label), position: 'relative' }}
              >
                <span>{label.label_name}</span>
                <span
                  className="remove-label"
                  title="Remove"
                  onClick={() => handleRemoveLabel(label.opportunity_label_id!)}
                >
                  ×
                </span>
              </li>
            ))}
          </ul>

          <div className="row-dates">
            <label className="input-label">
              Start:
              <input
                type="date"
                value={editedStartDate ? editedStartDate.substring(0, 10) : ''}
                onChange={e => setEditedStartDate(e.target.value)}
                className="modal-input"
              />
            </label>
            <label className="input-label">
              Due:
              <input
                type="date"
                value={editedDueDate ? editedDueDate.substring(0, 10) : ''}
                onChange={e => setEditedDueDate(e.target.value)}
                className="modal-input"
              />
            </label>
          </div>
        </section>
        <section>
          <h3 className="notes-header">Notes (supports URL links):</h3>

          {loadingNote ? (
            <div className="loading-note">Loading note...</div>
          ) : (
            <ReactQuill
              theme="snow"
              value={note}
              onChange={setNote}
              placeholder="Add or edit your notes here..."
            />
          )}
        </section>

      <div className="modal-actions">
        <button
          onClick={handleSave}
          className="modal-save-btn"
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
        {saveMessage && <span className="save-message">{saveMessage}</span>}
      </div>

<section>
  <div className="project-checklist-section">
    <span className="project-checklist-title">Project Checklist</span>
    <hr className="project-checklist-divider" />
  </div>
    <div className="project-checklist-input-row">
    <input
      type="text"
      className="project-checklist-input"
      maxLength={100}
      placeholder="Add new checklist item…"
      value={itemInput}
      onChange={e => setItemInput(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter' && !addingItem) handleAddItem();
      }}
      disabled={addingItem}
    />

  </div>

  <ul className="project-checklist-list">
  {items.length === 0 && (
    <li style={{ color: '#999', fontSize: 13, padding: 8 }}>
      No checklist items yet.
    </li>
  )}
  {items.map(item => (
    <li
      key={item.item_id}
      className={`project-checklist-item${editingItemId === item.item_id ? ' editing' : ''}`}
      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
    >
      {/* Radio Button for Flag */}
      <span
        style={{
          display: 'inline-block',
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: item.item_rank === 1 ? '#e74c3c' : '#fff',  // Red or white
          border: `2.5px solid ${item.item_rank === 1 ? '#e74c3c' : '#aaa'}`,
          cursor: 'pointer',
          transition: 'background 0.2s, border 0.2s',
        }}
        title={item.item_rank === 1 ? "Unflag item" : "Flag item"}
        onClick={() => handleRankToggle(item)}
        tabIndex={0}
        aria-label="Toggle flag"
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') handleRankToggle(item);
        }}
      ></span>

      {editingItemId === item.item_id ? (
        <>
          <input
            type="text"
            className="project-checklist-input"
            value={editingText}
            maxLength={100}
            autoFocus
            onChange={e => setEditingText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSaveEditItem(item.item_id);
              if (e.key === 'Escape') handleCancelEdit();
            }}
            disabled={deletingItemId === item.item_id}
          />
          <button
            className="icon-btn save"
            title="Save"
            onClick={() => handleSaveEditItem(item.item_id)}
            disabled={deletingItemId === item.item_id}
          >
            <CheckIcon />
          </button>
          <button
            className="icon-btn cancel"
            title="Cancel"
            onClick={handleCancelEdit}
            disabled={deletingItemId === item.item_id}
          >
            <XMarkIcon />
          </button>
        </>
      ) : (
        <>
          <span
            className={`checklist-text${deletingItemId === item.item_id ? ' strike' : ''}`}
            onClick={() => !deletingItemId && handleEditItem(item)}
            tabIndex={0}
            role="button"
            aria-label="Edit checklist item"
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') handleEditItem(item);
            }}
          >
            {item.item_description}
          </span>
          <button
            className="icon-btn edit"
            title="Edit"
            disabled={deletingItemId === item.item_id}
            onClick={() => handleEditItem(item)}
            aria-label="Edit checklist item"
          >
            <PencilSquareIcon />
          </button>
          <button
            className="icon-btn delete"
            title="Delete"
            disabled={deletingItemId === item.item_id}
            onClick={() => handleDeleteItem(item.item_id)}
            aria-label="Delete checklist item"
          >
            <TrashIcon />
          </button>
        </>
      )}
    </li>
  ))}
</ul>


</section>


      </div>
    </div>
  );
};

export default OpportunitySheetModal;
